/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// ╔═══════════════════════════════════════════════════════════════════╗
// ║ FASE2-SCHEMA-01 · ESTA RAMA NO ES DESPLEGABLE A main TODAVÍA      ║
// ╚═══════════════════════════════════════════════════════════════════╝
//
// Los 15 listing fields de este archivo declaran
// `listingTypeConfig.limitToListingTypeIds: true`, para separar el
// anuncio de negocio en venta (`free-listing`) del mandato de compra
// (`mandato-compra`).
//
// PROBLEMA CONOCIDO, NO ES UN BUG DEL ESQUEMA:
// En /s, `currentListingType` es SIEMPRE [] porque nada resuelve
// `pub_listingType`:
//   - listingTypeFilter.enabled = false (asset de Console, que además
//     pisa el valor local de configSearch.js en mergeSearchConfig),
//     así que no hay control que lo fije;
//   - validURLParamForListingTypeData devuelve {} sin ese query param;
//   - y con [] , isFieldForListingType es false para cualquier campo
//     limitado (fieldHelpers.js:76 — `[].includes(cid)` nunca es true).
// Consecuencia: el sidebar del SearchPage se queda sin filtros y los
// pub_* de las URLs ya indexadas se caen de la búsqueda
// (SearchPage.shared.js, pickListingFieldFilters y
// omitLimitedListingFieldParams).
//
// Es una DEPENDENCIA DEL SPRINT DEL BUSCADOR, que traerá el toggle
// oferta/demanda y con él la resolución del listing type. Hasta
// entonces esta rama se queda sin mergear: el esquema es correcto, lo
// que falta es quien resuelva el tipo en el buscador.
//
// ── PRINCIPIO DE CAMPOS DUALES (FASE2-SCHEMA-01.1) ──
// El anuncio de venta guarda PUNTOS; el mandato de compra guarda
// REGIONES: conjuntos en las dimensiones sin orden (provincia →
// provincias, modalidad → modalidades, tipo_operacion → tiposOperacion)
// e intervalos en las ordenadas (facturacion → facturacionIdxMin/Max,
// empleados → empleadosMin/Max, antiguedad → antiguedadIdxMin/Max).
// Cada par usa CLAVES DISTINTAS y no comparte una sola: el search
// schema de Sharetribe se define por clave y es global al marketplace,
// así que una misma clave no puede tener dos formas (enum en un tipo de
// anuncio y multi-enum o long en el otro). `sector` es la excepción
// deliberada: sigue compartido mientras producto decide el nivel
// jerárquico al que se busca.
//
// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be 'public', 'private', or 'metadata'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 *                                  For hierarchical enums (e.g. sector), each option may include
 *                                  a `suboptions` array with the same { option, label } shape;
 *                                  the SearchPage tree filter consumes this shape directly
 *                                  (FieldSelectTree-compatible).
 */
// ─────────────────────────────────────────────────────────────────────
// FASE2-SCHEMA-01 · Escala única de facturación
// ─────────────────────────────────────────────────────────────────────
// Fuente única de la escala de facturación: label + ordinal. De aquí
// derivan el enum `facturacion` (enumOptions) y los campos long
// `facturacionIdx`, `facturacionIdxMin` y `facturacionIdxMax`.
//
// ⚠️ LOS ORDINALES SON APPEND-ONLY.
// Los tramos se añaden SIEMPRE al final, nunca en medio, y los `idx`
// NUNCA se renumeran. Los anuncios ya publicados guardan el ordinal en
// pub_facturacionIdx: renumerar cambiaría el significado del dato ya
// persistido (un anuncio de "0 – 250.000 €" pasaría a leerse como otro
// tramo) sin que nada en la base de datos lo delate.
//
// Nota: los slugs canónicos viven además en FACTURACION_RANGES
// (configFilters.js), que alimenta facturacionIdxForOption. Ambas listas
// deben mantenerse en el mismo orden. Unificarlas requiere tocar
// configFilters.js, fuera del alcance de este sprint.
//
// lowerBound / upperBound: los límites del tramo EN MILES de €, la misma
// unidad que ya parsea facturacionTramoBounds() del slug y que formatea
// formatFacturacionLimitK(). Se declaran explícitos para no depender de que el
// slug siga siendo parseable, y por el mismo motivo que en ANTIGUEDAD_SCALE:
// la lectura del rango usa la cota, no la etiqueta. Último tramo abierto.
export const FACTURACION_SCALE = [
  { option: '0-250k',       label: '0 – 250.000 €',       lowerBound: 0,     upperBound: 250,   idx: 1 },
  { option: '250-500k',     label: '250.000 – 500.000 €', lowerBound: 250,   upperBound: 500,   idx: 2 },
  { option: '500-1000k',    label: '500.000 € – 1 M€',    lowerBound: 500,   upperBound: 1000,  idx: 3 },
  { option: '1000-1500k',   label: '1 – 1,5 M€',          lowerBound: 1000,  upperBound: 1500,  idx: 4 },
  { option: '1500-2000k',   label: '1,5 – 2 M€',          lowerBound: 1500,  upperBound: 2000,  idx: 5 },
  { option: '2000-3000k',   label: '2 – 3 M€',            lowerBound: 2000,  upperBound: 3000,  idx: 6 },
  { option: '3000-5000k',   label: '3 – 5 M€',            lowerBound: 3000,  upperBound: 5000,  idx: 7 },
  { option: '5000-10000k',  label: '5 – 10 M€',           lowerBound: 5000,  upperBound: 10000, idx: 8 },
  { option: '10000-25000k', label: '10 – 25 M€',          lowerBound: 10000, upperBound: 25000, idx: 9 },
  { option: 'mas-25000k',   label: 'Más de 25 M€',        lowerBound: 25000, upperBound: null,  idx: 10 },
];

// enumOptions del campo `facturacion`, derivadas de la escala.
const facturacionEnumOptions = FACTURACION_SCALE.map(({ option, label }) => ({ option, label }));

// Límites del ordinal, derivados de la escala (hoy 1..10). Al ser
// append-only, el máximo crece solo cuando se añade un tramo al final.
export const FACTURACION_IDX_MIN = FACTURACION_SCALE[0].idx;
export const FACTURACION_IDX_MAX = FACTURACION_SCALE[FACTURACION_SCALE.length - 1].idx;

// Rango del valor visible de `empleados` (long): el nº de empleados en
// sí, no su posición en la escala.
export const EMPLEADOS_RANGE = { minimum: 1, maximum: 200 };

// ─────────────────────────────────────────────────────────────────────
// FASE2-SCHEMA-01.2 · Escala única de empleados
// ─────────────────────────────────────────────────────────────────────
// Fuente única de los tramos de plantilla: label visible, límites del
// tramo y ordinal. De aquí derivan el select del wizard (vía
// EMPLEADOS_OPTIONS en configFilters.js), el long calculado
// `empleadosIdx` (free-listing) y los long `empleadosMin` /
// `empleadosMax` (mandato-compra).
//
// Los tramos y labels son EXACTAMENTE los que ya mostraba el
// desplegable antes de esta escala (EMPLEADOS_OPTIONS): no cambia nada
// de lo que ve el vendedor.
//
// ⚠️ LOS ORDINALES SON APPEND-ONLY.
// Los tramos se añaden SIEMPRE al final, nunca en medio, y los `idx`
// NUNCA se renumeran. Los anuncios ya publicados guardan el ordinal en
// pub_empleadosIdx (y los mandatos en pub_empleadosMin/Max): renumerar
// cambiaría el significado del dato ya persistido sin que nada en la
// base de datos lo delate.
//
// Nota: `lowerBound` es lo que sigue guardándose en pub_empleados (el
// valor visible). El ordinal vive aparte, en pub_empleadosIdx, porque
// el matching con el mandato debe comparar POSICIONES de la escala, no
// números de empleados: así añadir un tramo no reinterpreta los datos.
export const EMPLEADOS_SCALE = [
  { lowerBound: 1,   upperBound: 5,   label: '1 – 5',    idx: 1 },
  { lowerBound: 6,   upperBound: 15,  label: '6 – 15',   idx: 2 },
  { lowerBound: 16,  upperBound: 50,  label: '16 – 50',  idx: 3 },
  { lowerBound: 51,  upperBound: 100, label: '51 – 100', idx: 4 },
  { lowerBound: 101, upperBound: 200, label: '+100',     idx: 5 },
];

// Límites del ordinal, derivados de la escala (hoy 1..5).
export const EMPLEADOS_IDX_MIN = EMPLEADOS_SCALE[0].idx;
export const EMPLEADOS_IDX_MAX = EMPLEADOS_SCALE[EMPLEADOS_SCALE.length - 1].idx;

// ─────────────────────────────────────────────────────────────────────
// FASE2-SCHEMA-01.1 · Escala única de antigüedad
// ─────────────────────────────────────────────────────────────────────
// Fuente única de la escala de antigüedad: label + ordinal. De aquí
// derivan el enum `antiguedad` (free-listing) y los long
// `antiguedadIdxMin` / `antiguedadIdxMax` (mandato-compra).
//
// ⚠️ LOS ORDINALES SON APPEND-ONLY.
// Los tramos se añaden SIEMPRE al final, nunca en medio, y los `idx`
// NUNCA se renumeran. Los mandatos ya publicados guardan el ordinal en
// pub_antiguedadIdxMin/Max: renumerar cambiaría el significado del dato
// ya persistido sin que nada en la base de datos lo delate.
//
// lowerBound / upperBound: los límites del tramo EN AÑOS. Existen para que la
// lectura del rango use la cota y no la etiqueta: anteponer "Desde" a una
// etiqueta que ya es un rango produce frases ambiguas ("Desde 2 – 5 años" se
// lee como "de 2 a 5"). El último tramo es abierto por arriba: upperBound null.
export const ANTIGUEDAD_SCALE = [
  { option: 'menos-2', label: '< 2 años',     lowerBound: 0,  upperBound: 2,    idx: 1 },
  { option: '2-5',     label: '2 – 5 años',   lowerBound: 2,  upperBound: 5,    idx: 2 },
  { option: '5-10',    label: '5 – 10 años',  lowerBound: 5,  upperBound: 10,   idx: 3 },
  { option: '10-20',   label: '10 – 20 años', lowerBound: 10, upperBound: 20,   idx: 4 },
  { option: 'mas-20',  label: '> 20 años',    lowerBound: 20, upperBound: null, idx: 5 },
];

// enumOptions del campo `antiguedad`, derivadas de la escala.
const antiguedadEnumOptions = ANTIGUEDAD_SCALE.map(({ option, label }) => ({ option, label }));

// Límites del ordinal, derivados de la escala (hoy 1..5).
export const ANTIGUEDAD_IDX_MIN = ANTIGUEDAD_SCALE[0].idx;
export const ANTIGUEDAD_IDX_MAX = ANTIGUEDAD_SCALE[ANTIGUEDAD_SCALE.length - 1].idx;

// ─────────────────────────────────────────────────────────────────────
// FASE2-SCHEMA-01.5 · Escala única de EBITDA
// ─────────────────────────────────────────────────────────────────────
// Solo existe en el ANUNCIO DE COMPRA: el comprador declara qué
// rentabilidad busca. El de venta no lo declara, así que aquí no hay par
// dual — solo el intervalo `ebitdaIdxMin` / `ebitdaIdxMax`.
//
// ⚠️ LOS ORDINALES SON APPEND-ONLY.
// Los tramos se añaden SIEMPRE al final, nunca en medio, y los `idx`
// NUNCA se renumeran. Los mandatos ya publicados guardan el ordinal en
// pub_ebitdaIdxMin/Max: renumerar cambiaría el significado del dato ya
// persistido sin que nada en la base de datos lo delate.
//
// lowerBound / upperBound: los límites del tramo EN MILES de €, la misma
// unidad que FACTURACION_SCALE, para poder reutilizar sus formateadores
// de importe. Existen para que la lectura del rango use la cota y no la
// etiqueta. El último tramo es abierto por arriba: upperBound null.
export const EBITDA_SCALE = [
  { label: '0 – 50 K€',       lowerBound: 0,    upperBound: 50,   idx: 1 },
  { label: '50 – 100 K€',     lowerBound: 50,   upperBound: 100,  idx: 2 },
  { label: '100 – 250 K€',    lowerBound: 100,  upperBound: 250,  idx: 3 },
  { label: '250 – 500 K€',    lowerBound: 250,  upperBound: 500,  idx: 4 },
  { label: '500 K€ – 1 M€',   lowerBound: 500,  upperBound: 1000, idx: 5 },
  { label: '1 – 2,5 M€',      lowerBound: 1000, upperBound: 2500, idx: 6 },
  { label: '2,5 – 5 M€',      lowerBound: 2500, upperBound: 5000, idx: 7 },
  { label: 'Más de 5 M€',     lowerBound: 5000, upperBound: null, idx: 8 },
];

// Límites del ordinal, derivados de la escala (hoy 1..8).
export const EBITDA_IDX_MIN = EBITDA_SCALE[0].idx;
export const EBITDA_IDX_MAX = EBITDA_SCALE[EBITDA_SCALE.length - 1].idx;

// ─────────────────────────────────────────────────────────────────────
// FASE2-SCHEMA-01.1 · Opciones compartidas por los pares duales
// ─────────────────────────────────────────────────────────────────────
// Fuente única de `modalidad` (enum, free-listing) y `modalidades`
// (multi-enum, mandato-compra).
export const MODALIDAD_OPTIONS = [
  { option: 'online',     label: '100% online' },
  { option: 'hibrida',    label: 'Online + físico' },
  { option: 'presencial', label: '100% presencial' },
];

// Fuente única de `tipo_operacion` (enum, free-listing) y
// `tiposOperacion` (multi-enum, mandato-compra).
// FASE2-WIZARD-2c: fuente única del identificador del anuncio de compra.
// Vivía duplicado en mandatoSteps.js (borrado al fundir el wizard en un solo
// paso) y en ListingPage/mandatoDetails.js.
export const MANDATO_LISTING_TYPE = 'mandato-compra';

// FASE2-BUSCADOR-3e: el anuncio de venta. Vivía suelto en NewListingPage; se
// sube aquí porque configSearch necesita nombrarlo para acotar por feed las
// opciones de ordenación.
export const FREE_LISTING_TYPE = 'free-listing';

// FASE2-SCHEMA-01.4: tres valores. `busqueda-inversor` se partió en dos —
// minoritario y mayoritario— porque son operaciones distintas para las dos
// partes: quien entra con el 20% no busca lo mismo que quien entra con el 60%.
// El slug retirado sobrevive en los datos hasta que corra
// scripts/migrate-tipo-operacion-tres-valores.js.
//
// Las etiquetas de aquí son las del ANUNCIO DE VENTA (quien ofrece). El
// anuncio de compra las lee desde su propio lado vía localizeEnumOptions
// (util/listingFieldLabels.js) con las claves ListingFields.tiposOperacion.*
// de es.json: 'venta-total' es "Venta total" para quien vende y "Compra del
// negocio completo" para quien compra. El SLUG es el mismo en los dos: es lo
// que permite cruzarlos.
export const TIPO_OPERACION_OPTIONS = [
  { option: 'venta-total',           label: 'Venta total' },
  { option: 'inversor-minoritario',  label: 'Busco inversor minoritario' },
  { option: 'inversor-mayoritario',  label: 'Busco inversor mayoritario' },
];

// ─────────────────────────────────────────────────────────────────────
// FASE2-SCHEMA-01 · Provincias
// ─────────────────────────────────────────────────────────────────────
// Las 52 provincias (50 + Ceuta y Melilla). Fuente única del enum
// `provincia` (free-listing, una sola) y del multi-enum `provincias`
// (mandato-compra, varias).
export const PROVINCIA_OPTIONS = [
  { option: 'alava',          label: 'Álava' },
  { option: 'albacete',       label: 'Albacete' },
  { option: 'alicante',       label: 'Alicante' },
  { option: 'almeria',        label: 'Almería' },
  { option: 'asturias',       label: 'Asturias' },
  { option: 'avila',          label: 'Ávila' },
  { option: 'badajoz',        label: 'Badajoz' },
  { option: 'barcelona',      label: 'Barcelona' },
  { option: 'burgos',         label: 'Burgos' },
  { option: 'caceres',        label: 'Cáceres' },
  { option: 'cadiz',          label: 'Cádiz' },
  { option: 'cantabria',      label: 'Cantabria' },
  { option: 'castellon',      label: 'Castellón' },
  { option: 'ceuta',          label: 'Ceuta' },
  { option: 'ciudad-real',    label: 'Ciudad Real' },
  { option: 'cordoba',        label: 'Córdoba' },
  { option: 'cuenca',         label: 'Cuenca' },
  { option: 'girona',         label: 'Girona' },
  { option: 'granada',        label: 'Granada' },
  { option: 'guadalajara',    label: 'Guadalajara' },
  { option: 'guipuzcoa',      label: 'Guipúzcoa' },
  { option: 'huelva',         label: 'Huelva' },
  { option: 'huesca',         label: 'Huesca' },
  { option: 'islas-baleares', label: 'Islas Baleares' },
  { option: 'jaen',           label: 'Jaén' },
  { option: 'la-coruna',      label: 'La Coruña' },
  { option: 'la-rioja',       label: 'La Rioja' },
  { option: 'las-palmas',     label: 'Las Palmas' },
  { option: 'leon',           label: 'León' },
  { option: 'lleida',         label: 'Lleida' },
  { option: 'lugo',           label: 'Lugo' },
  { option: 'madrid',         label: 'Madrid' },
  { option: 'malaga',         label: 'Málaga' },
  { option: 'melilla',        label: 'Melilla' },
  { option: 'murcia',         label: 'Murcia' },
  { option: 'navarra',        label: 'Navarra' },
  { option: 'ourense',        label: 'Ourense' },
  { option: 'palencia',       label: 'Palencia' },
  { option: 'pontevedra',     label: 'Pontevedra' },
  { option: 'salamanca',      label: 'Salamanca' },
  { option: 'santa-cruz-tenerife', label: 'Santa Cruz de Tenerife' },
  { option: 'segovia',        label: 'Segovia' },
  { option: 'sevilla',        label: 'Sevilla' },
  { option: 'soria',          label: 'Soria' },
  { option: 'tarragona',      label: 'Tarragona' },
  { option: 'teruel',         label: 'Teruel' },
  { option: 'toledo',         label: 'Toledo' },
  { option: 'valencia',       label: 'Valencia' },
  { option: 'valladolid',     label: 'Valladolid' },
  { option: 'vizcaya',        label: 'Vizcaya' },
  { option: 'zamora',         label: 'Zamora' },
  { option: 'zaragoza',       label: 'Zaragoza' },
];

// FASE2-BUSCADOR-3d: el comodín de ámbito nacional. El filtro del sidebar lo
// añade a la provincia elegida, porque un mandato que busca en toda España
// también encaja con un negocio de Álava y si no desaparecería del resultado.
export const PROVINCIA_TODA_ESPANA = 'toda-espana';

// Opciones del multi-enum `provincias`: las 52 + el comodín "Toda
// España", que va primero por ser una opción de ámbito, no una provincia.
const PROVINCIAS_OPTIONS = [
  { option: PROVINCIA_TODA_ESPANA, label: 'Toda España' },
  ...PROVINCIA_OPTIONS,
];

export const listingFields = [
  // ── Campo admin-only: verificado (solo filtro, sin saveConfig) ──
  {
    key: 'verificado',
    scope: 'public',
    schemaType: 'boolean',
    // FASE2-SCHEMA-01: la verificación es del negocio en venta.
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    filterConfig: {
      indexForSearch: true,
      // OCULTAR-VERIFICACION: filtro retirado del buscador. Para revertir,
      // volver a poner showFilter: true (nada más que tocar aquí).
      // Nota: showFilter: false lo retira del sidebar (isFilterEnabled), pero
      // por sí solo NO basta para neutralizar `?pub_verificado=true` en la URL:
      // loadData no pasa por validFilterParams. Lo que cierra esa vía es que
      // prepareBooleanParam (SearchPage.duck.js) solo convierte booleanos cuyo
      // filtro está habilitado; sin esa conversión la API ignora el string.
      showFilter: false,
      label: 'Negocios verificados',
      group: 'primary',
    },
    showConfig: {
      label: 'Verificado',
      isDetail: false,
    },
  },

  // ---------------------------------------------------------------
  // Uroborox — campos de negocio (todos públicos, obligatorios)
  // DATAMODEL-01: nuevo esquema cerrado por el CEO.
  // ---------------------------------------------------------------

  // Sector — taxonomía jerárquica 11 madre + 55 subsectores.
  // El SearchPage seguirá renderizando un SelectMultipleFilter plano
  // (sólo se ven los 11 madre) hasta que el siguiente prompt entregue
  // el componente multi-select tree. El motor `FieldSelectTree` ya
  // entiende el shape { option, label, suboptions }.
  {
    key: 'sector',
    scope: 'public',
    schemaType: 'enum',
    // FASE2-SCHEMA-01: el vendedor declara su sector; el comprador,
    // el sector que busca.
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing', 'mandato-compra'],
    },
    enumOptions: [
      {
        option: 'hosteleria-y-restauracion',
        label: 'Hostelería y restauración',
        suboptions: [
          { option: 'bar-y-cafeteria',                  label: 'Bar y cafetería' },
          { option: 'restaurante',                       label: 'Restaurante' },
          { option: 'catering-y-eventos-gastronomicos', label: 'Catering y eventos gastronómicos' },
          { option: 'pasteleria-y-panaderia',           label: 'Pastelería y panadería' },
          { option: 'pubs-y-discotecas', label: 'Pubs y discotecas' },
        ],
      },
      {
        option: 'turismo-y-alojamiento',
        label: 'Turismo y alojamiento',
        suboptions: [
          { option: 'hotel-y-alojamiento-turistico', label: 'Hotel y alojamiento turístico' },
          { option: 'agencia-de-viajes',             label: 'Agencia de viajes' },
          { option: 'turismo-activo-y-experiencias', label: 'Turismo activo y experiencias' },
        ],
      },
      {
        option: 'comercio',
        label: 'Comercio',
        suboptions: [
          { option: 'moda-y-complementos',            label: 'Moda y complementos' },
          { option: 'alimentacion',                   label: 'Alimentación' },
          { option: 'ferreteria-hogar-y-decoracion',  label: 'Ferretería, hogar y decoración' },
          { option: 'belleza-y-cosmetica',            label: 'Belleza y cosmética' },
          { option: 'mascotas',                       label: 'Mascotas' },
          { option: 'deporte',                        label: 'Deporte' },
          { option: 'comercio-especializado',         label: 'Comercio especializado' },
          { option: 'distribucion-mayorista-y-suministros-profesionales', label: 'Distribución mayorista y suministros profesionales' },
          { option: 'estancos', label: 'Estancos' },
          { option: 'gasolineras-y-estaciones-de-servicio', label: 'Gasolineras y estaciones de servicio' },
          { option: 'vending-y-venta-automatica', label: 'Vending y venta automática' },
        ],
      },
      {
        option: 'servicios-profesionales',
        label: 'Servicios profesionales',
        suboptions: [
          { option: 'despacho-juridico',                  label: 'Despacho jurídico' },
          { option: 'asesoria-y-gestoria',                label: 'Asesoría y gestoría' },
          { option: 'consultoria-empresarial',            label: 'Consultoría empresarial' },
          { option: 'servicios-financieros-y-seguros',    label: 'Servicios financieros y seguros' },
          { option: 'recursos-humanos-y-seleccion',       label: 'Recursos humanos y selección' },
          { option: 'agencia-inmobiliaria',               label: 'Agencia inmobiliaria' },
          { option: 'agencia-de-marketing-y-comunicacion', label: 'Agencia de marketing y comunicación' },
        ],
      },
      {
        option: 'salud-y-bienestar',
        label: 'Salud y bienestar',
        suboptions: [
          { option: 'clinica-medica',              label: 'Clínica médica' },
          { option: 'clinica-dental',              label: 'Clínica dental' },
          { option: 'centro-de-estetica',          label: 'Centro de estética' },
          { option: 'peluqueria-y-barberia',       label: 'Peluquería y barbería' },
          { option: 'gimnasio-y-centro-deportivo', label: 'Gimnasio y centro deportivo' },
          { option: 'fisioterapia-y-rehabilitacion', label: 'Fisioterapia y rehabilitación' },
          { option: 'farmacia-y-parafarmacia',     label: 'Farmacia y parafarmacia' },
          { option: 'veterinaria',                 label: 'Veterinaria' },
          { option: 'laboratorios-dentales-y-protesis', label: 'Laboratorios dentales y prótesis' },
        ],
      },
      {
        option: 'software-y-tecnologia',
        label: 'Software y tecnología',
        suboptions: [
          { option: 'saas-marketplace-y-producto-digital', label: 'SaaS, marketplace y producto digital' },
          { option: 'agencia-de-desarrollo-a-medida',      label: 'Agencia de desarrollo a medida' },
          { option: 'servicios-it',                         label: 'Servicios IT (hosting, ciberseguridad, infraestructura)' },
        ],
      },
      {
        option: 'industria-y-manufactura',
        label: 'Industria y manufactura',
        suboptions: [
          { option: 'taller-mecanico',                       label: 'Taller mecánico' },
          { option: 'carpinteria-y-madera',                  label: 'Carpintería y madera' },
          { option: 'metalurgia-y-construcciones-metalicas', label: 'Metalurgia y construcciones metálicas' },
          { option: 'textil-y-confeccion',                   label: 'Textil y confección' },
          { option: 'produccion-alimentaria',                label: 'Producción alimentaria' },
          { option: 'produccion-primaria',                   label: 'Producción primaria' },
          { option: 'produccion-industrial-especializada',   label: 'Producción industrial especializada' },
        ],
      },
      {
        option: 'construccion-y-reformas',
        label: 'Construcción y reformas',
        suboptions: [
          { option: 'empresa-constructora',  label: 'Empresa constructora' },
          { option: 'reformas-integrales',   label: 'Reformas integrales' },
          { option: 'instalaciones',         label: 'Instalaciones (fontanería, climatización, electricidad, renovables)' },
          { option: 'pintura-y-acabados',    label: 'Pintura y acabados' },
          { option: 'promotora-inmobiliaria', label: 'Promotora inmobiliaria' },
        ],
      },
      {
        option: 'transporte-y-logistica',
        label: 'Transporte y logística',
        suboptions: [
          { option: 'transporte-de-mercancias',     label: 'Transporte de mercancías' },
          { option: 'mensajeria-y-ultima-milla',    label: 'Mensajería y última milla' },
          { option: 'almacenamiento-y-warehousing', label: 'Almacenamiento y warehousing' },
          { option: 'transporte-de-pasajeros',      label: 'Transporte de pasajeros (taxi, VTC)' },
        ],
      },
      {
        option: 'educacion-y-formacion',
        label: 'Educación y formación',
        // sin subsectores: el sector madre se selecciona como hoja
      },
      {
        option: 'servicios-para-empresas-y-particulares',
        label: 'Servicios para empresas y particulares',
        suboptions: [
          { option: 'limpieza-y-mantenimiento',         label: 'Limpieza y mantenimiento' },
          { option: 'seguridad',                        label: 'Seguridad' },
          { option: 'jardineria-y-paisajismo',          label: 'Jardinería y paisajismo' },
          { option: 'mudanzas',                         label: 'Mudanzas' },
          { option: 'lavanderia-y-tintoreria',          label: 'Lavandería y tintorería' },
          { option: 'servicios-funerarios',             label: 'Servicios funerarios' },
          { option: 'gestion-de-residuos-y-reciclaje',  label: 'Gestión de residuos y reciclaje' },
          { option: 'alquiler-de-maquinaria-y-equipos', label: 'Alquiler de maquinaria y equipos' },
          { option: 'produccion-de-eventos-y-servicios-audiovisuales', label: 'Producción de eventos y servicios audiovisuales' },
        ],
      },
    ],
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Sector',
      group: 'primary',
      searchable: true,
      searchPlaceholder: 'Buscar sector…',
    },
    showConfig: {
      label: 'Sector',
      isDetail: true,
    },
    saveConfig: {
      label: 'Sector',
      placeholderMessage: 'Selecciona el sector…',
      isRequired: true,
      requiredMessage: 'El sector es obligatorio.',
    },
  },

  {
    key: 'provincia',
    scope: 'public',
    schemaType: 'enum',
    // FASE2-SCHEMA-01: el negocio en venta está en UNA provincia. El
    // mandato de compra usa `provincias` (multi-enum) en su lugar.
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    // helpText (nivel raíz): se muestra SOLO en el formulario de publicar/
    // editar (CustomFieldEnum → FieldSelect → HelpText). No aparece en ficha,
    // filtro ni card. Ver reports/PROVINCIA_CAMPO_AUDIT.md §1.
    helpText:
      'Indica la provincia donde se ubica principalmente el negocio, aunque opere online o en varias zonas.',
    // FASE2-SCHEMA-01: derivado de PROVINCIA_OPTIONS (fuente única,
    // compartida con el multi-enum `provincias`).
    enumOptions: PROVINCIA_OPTIONS,
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Provincia de ubicación principal',
      group: 'primary',
      searchable: true,
      searchPlaceholder: 'Buscar provincia…',
    },
    showConfig: {
      label: 'Provincia de ubicación principal',
      isDetail: true,
    },
    saveConfig: {
      label: 'Provincia de ubicación principal',
      placeholderMessage: 'Selecciona la provincia…',
      isRequired: true,
      requiredMessage: 'La provincia es obligatoria.',
    },
  },

  // ORDEN-CANONICO: modalidad y tipo_operacion van tras provincia
  // (sector → provincia → modalidad → tipo_operacion → facturacion →
  // empleados → antiguedad). Este array es la fuente del orden en
  // wizard, ficha de detalle y filtros de SearchPage.

  // Forma de operar — DATAMODEL-01: nuevo enum, 3 opciones autoexplicativas.
  {
    key: 'modalidad',
    scope: 'public',
    schemaType: 'enum',
    // FASE2-SCHEMA-01.1: el negocio en venta opera de UNA forma (punto).
    // El mandato acepta varias y usa `modalidades` (multi-enum).
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    // FASE2-SCHEMA-01.1: derivado de MODALIDAD_OPTIONS (fuente única).
    enumOptions: MODALIDAD_OPTIONS,
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Forma de operar',
      group: 'primary',
    },
    showConfig: {
      label: 'Forma de operar',
      isDetail: true,
    },
    saveConfig: {
      label: 'Forma de operar',
      placeholderMessage: 'Selecciona la forma de operar…',
      isRequired: true,
      requiredMessage: 'La forma de operar es obligatoria.',
    },
  },

  // Tipo de operación — DATAMODEL-01: solo 2 valores. "Traspaso" no
  // se contempla. Slugs preservados (venta-total / busqueda-inversor)
  // para no invalidar URLs existentes.
  {
    key: 'tipo_operacion',
    scope: 'public',
    // FASE2-SCHEMA-01.4: pasa de enum a multi-enum. Un vendedor puede aceptar
    // varias operaciones a la vez —entrada minoritaria O mayoritaria, por
    // ejemplo— y obligarle a elegir una sola le dejaba fuera de búsquedas que
    // sí encajaban. Queda simétrico con `tiposOperacion` del mandato.
    //
    // ⚠️ EL SEARCH SCHEMA HAY QUE REGISTRARLO DE NUEVO: una clave no puede
    // cambiar de tipo en caliente. Hace falta `unset` del enum y `set` como
    // multi-enum — ver scripts/setup-search-schemas.sh.
    schemaType: 'multi-enum',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    // FASE2-SCHEMA-01.1: derivado de TIPO_OPERACION_OPTIONS (fuente única).
    enumOptions: TIPO_OPERACION_OPTIONS,
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tipo de operación',
      group: 'primary',
      // Basta con que una de las operaciones que ofrece el vendedor coincida
      // con lo que busca quien filtra. Mismo criterio que `tiposOperacion`.
      searchMode: 'has_any',
    },
    showConfig: {
      label: 'Tipo de operación',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tipo de operación',
      placeholderMessage: 'Selecciona una o varias…',
      isRequired: true,
      requiredMessage: 'Selecciona al menos un tipo de operación.',
    },
  },

  // Facturación — DATAMODEL-01: pasa de long a enum por tramos.
  // Los slugs son K-based unidad-única para que el orden alfabético
  // del backend no entre en juego. FACTURACION-IDX-01 (3b): la
  // ordenación y el filtro por rango se hacen vía pub_facturacionIdx
  // (long 1..10 derivado del orden de estas enumOptions).
  {
    key: 'facturacion',
    scope: 'public',
    schemaType: 'enum',
    // FASE2-SCHEMA-01: la factura la declara el negocio en venta. El
    // mandato de compra expresa su umbral con facturacionIdxMin/Max.
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    // FASE2-SCHEMA-01: derivado de FACTURACION_SCALE (fuente única).
    enumOptions: facturacionEnumOptions,
    filterConfig: {
      indexForSearch: true,
      // FACTURACION-IDX-01 (3b): el filtro visible de facturación pasa a
      // ser el Desde–Hasta sobre facturacionIdx (campo siguiente). Este
      // enum sigue alimentando wizard y ficha de detalle.
      showFilter: false,
      label: 'Facturación anual',
      group: 'primary',
    },
    showConfig: {
      label: 'Facturación anual',
      isDetail: true,
    },
    saveConfig: {
      label: 'Facturación anual',
      placeholderMessage: 'Selecciona el tramo…',
      isRequired: true,
      requiredMessage: 'La facturación anual es obligatoria.',
    },
  },

  // FACTURACION-IDX-01 (3b): índice ordinal 1..10 del tramo de
  // facturación (posición en enumOptions/FACTURACION_RANGES). Campo
  // CALCULADO: lo deriva el submit del wizard (EditListingDetailsPanel)
  // y nunca se edita a mano — CustomExtendedDataField lo omite. Existe
  // como field config para que pub_facturacionIdx sea un query param
  // válido en SearchPage (filtro Desde–Hasta) y ordenable (long).
  {
    key: 'facturacionIdx',
    scope: 'public',
    schemaType: 'long',
    // FASE2-SCHEMA-01: espejo de `facturacion`, mismo tipo de anuncio.
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    // FASE2-SCHEMA-01: límites derivados de FACTURACION_SCALE (1..10).
    numberConfig: { minimum: FACTURACION_IDX_MIN, maximum: FACTURACION_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      label: 'Facturación anual',
      group: 'primary',
    },
    showConfig: {
      label: 'Facturación anual',
      isDetail: false,
    },
    saveConfig: {
      label: 'Facturación anual',
    },
  },

  // Empleados — sigue como long. EMPLEADOS-TRAMOS-01 (3a): el wizard lo
  // edita como select de tramos (EMPLEADOS_SCALE) y persiste el límite
  // inferior del tramo (1/6/16/51/101), sin redondeo.
  // FASE2-SCHEMA-01.2: es el valor VISIBLE (card y ficha). La ordenación
  // y el filtro por rango pasan a `empleadosIdx` (campo siguiente), igual
  // que facturacion → facturacionIdx.
  {
    key: 'empleados',
    scope: 'public',
    schemaType: 'long',
    // FASE2-SCHEMA-01: la plantilla la declara el negocio en venta. El
    // mandato de compra expresa su umbral con empleadosMin/Max.
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    numberConfig: EMPLEADOS_RANGE,
    // FASE2-SCHEMA-01.2: el helpText anterior prometía un redondeo "al
    // múltiplo de 5 más cercano" que dejó de existir en 0363f8452, cuando
    // el input libre pasó a ser este desplegable de tramos cerrados.
    helpText: 'Selecciona el tramo de plantilla del negocio.',
    filterConfig: {
      indexForSearch: true,
      // FASE2-SCHEMA-01.2: el filtro visible pasa a ser el Desde–Hasta
      // sobre empleadosIdx. Este campo sigue indexado (la card y la ficha
      // lo leen, y las URLs con pub_empleados siguen siendo válidas).
      showFilter: false,
      label: 'Nº empleados',
      group: 'primary',
    },
    showConfig: {
      label: 'Nº empleados',
      isDetail: true,
    },
    saveConfig: {
      label: 'Nº de empleados',
      placeholderMessage: 'Selecciona el tramo',
      isRequired: true,
      requiredMessage: 'El número de empleados es obligatorio.',
    },
  },

  // FASE2-SCHEMA-01.2: índice ordinal 1..5 del tramo de plantilla
  // (posición en EMPLEADOS_SCALE). Campo CALCULADO: lo deriva el submit
  // del wizard (EditListingDetailsPanel) desde el tramo elegido y nunca
  // se edita a mano — CustomExtendedDataField lo omite. Existe como field
  // config para que pub_empleadosIdx sea un query param válido en
  // SearchPage (filtro Desde–Hasta) y ordenable (long).
  // Mismo patrón que facturacion → facturacionIdx.
  {
    key: 'empleadosIdx',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    numberConfig: { minimum: EMPLEADOS_IDX_MIN, maximum: EMPLEADOS_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      label: 'Nº empleados',
      group: 'primary',
    },
    showConfig: {
      label: 'Nº empleados',
      isDetail: false,
    },
    saveConfig: {
      label: 'Nº empleados',
    },
  },

  {
    key: 'antiguedad',
    scope: 'public',
    schemaType: 'enum',
    // FASE2-SCHEMA-01.1: el negocio en venta tiene UNA antigüedad (punto).
    // El mandato busca un intervalo y usa antiguedadIdxMin/Max.
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    // FASE2-SCHEMA-01.1: derivado de ANTIGUEDAD_SCALE (fuente única).
    enumOptions: antiguedadEnumOptions,
    filterConfig: {
      indexForSearch: true,
      // FASE2-SCHEMA-01.3: el filtro visible pasa a ser el Desde–Hasta
      // sobre antiguedadIdx. Este enum sigue indexado (la ficha lo lee y
      // las URLs con pub_antiguedad siguen siendo válidas) y sigue
      // alimentando wizard y ficha de detalle.
      showFilter: false,
      filterType: 'SelectMultipleFilter',
      label: 'Antigüedad',
      group: 'primary',
    },
    showConfig: {
      label: 'Antigüedad',
      isDetail: true,
    },
    saveConfig: {
      label: 'Antigüedad',
      placeholderMessage: 'Selecciona la antigüedad del negocio…',
      isRequired: true,
      requiredMessage: 'La antigüedad es obligatoria.',
    },
  },

  // FASE2-SCHEMA-01.3: índice ordinal 1..5 del tramo de antigüedad
  // (posición en ANTIGUEDAD_SCALE). Campo CALCULADO: lo deriva el submit
  // del wizard (EditListingDetailsPanel) desde el tramo elegido y nunca
  // se edita a mano — CustomExtendedDataField lo omite. Existe como
  // field config para que pub_antiguedadIdx sea un query param válido en
  // SearchPage (filtro Desde–Hasta) y comparable por rango.
  //
  // POR QUÉ HACE FALTA: el mandato guarda su intervalo como
  // antiguedadIdxMin/Max (long). Cruzarlo con el enum `antiguedad` es
  // imposible —un slug no se compara con un rango numérico—, así que sin
  // este ordinal el matching por antigüedad no puede existir.
  // Mismo patrón que facturacion → facturacionIdx y empleados →
  // empleadosIdx.
  {
    key: 'antiguedadIdx',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['free-listing'],
    },
    // FASE2-SCHEMA-01.3: límites derivados de ANTIGUEDAD_SCALE (1..5).
    numberConfig: { minimum: ANTIGUEDAD_IDX_MIN, maximum: ANTIGUEDAD_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      label: 'Antigüedad',
      group: 'primary',
    },
    showConfig: {
      label: 'Antigüedad',
      isDetail: false,
    },
    saveConfig: {
      label: 'Antigüedad',
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // FASE2-SCHEMA-01 · Campos exclusivos del mandato de compra
  // ═══════════════════════════════════════════════════════════════════
  // Solo esquema: este sprint no toca wizard, buscador, tarjetas ni
  // ficha. Todos van con showFilter: false a propósito — el sidebar del
  // SearchPage se arma con groupListingFieldConfigs(activeListingTypes),
  // y activeListingTypes son TODOS los tipos del marketplace, así que un
  // showFilter: true aquí colaría estos filtros en el buscador de
  // negocios actual. Se activarán en el sprint del buscador.

  // Provincias que busca el comprador. Multi-enum (varias) frente al
  // enum `provincia` del negocio en venta (una sola).
  {
    key: 'provincias',
    scope: 'public',
    schemaType: 'multi-enum',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    // Las 52 de PROVINCIA_OPTIONS + el comodín 'toda-espana'.
    enumOptions: PROVINCIAS_OPTIONS,
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: '¿Dónde está tu negocio?',
      group: 'primary',
      searchable: true,
      searchPlaceholder: 'Buscar provincia…',
      // FASE2-SCHEMA-01.1: has_any (el defecto del API es has_all). Un
      // mandato guarda el CONJUNTO de provincias que le valen, así que
      // basta con que coincida una. Con has_all, "sevilla,toda-espana"
      // exigiría ambas y el comodín nunca casaría.
      searchMode: 'has_any',
    },
    showConfig: {
      label: 'Provincias de interés',
      isDetail: true,
    },
    saveConfig: {
      label: 'Provincias de interés',
      placeholderMessage: 'Selecciona una o varias provincias…',
      // FASE2-WIZARD-2b: obligatorio. Sin ámbito, el mandato no aparecería en
      // ninguna búsqueda por provincia.
      isRequired: true,
      requiredMessage: 'Elige al menos una provincia o marca "Toda España".',
    },
  },

  // Formas de operar que acepta el comprador. Multi-enum (varias) frente
  // al enum `modalidad` del negocio en venta (una sola).
  {
    key: 'modalidades',
    scope: 'public',
    schemaType: 'multi-enum',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    // FASE2-SCHEMA-01.1: mismas opciones que `modalidad` (fuente única).
    enumOptions: MODALIDAD_OPTIONS,
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Cómo opera tu negocio',
      group: 'primary',
      // has_any: basta con que una de las formas aceptadas coincida.
      searchMode: 'has_any',
    },
    showConfig: {
      label: 'Formas de operar aceptadas',
      isDetail: true,
    },
    saveConfig: {
      label: 'Formas de operar aceptadas',
      placeholderMessage: 'Selecciona una o varias…',
      // FASE2-WIZARD-2b: obligatorio. Un conjunto vacío sacaría al mandato
      // de toda búsqueda que filtrase por esta dimensión.
      isRequired: true,
      requiredMessage: 'Selecciona al menos una forma de operar.',
    },
  },

  // Tipos de operación que acepta el comprador. Multi-enum (varios)
  // frente al enum `tipo_operacion` del negocio en venta (uno solo).
  {
    key: 'tiposOperacion',
    scope: 'public',
    schemaType: 'multi-enum',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    // FASE2-SCHEMA-01.1: mismas opciones que `tipo_operacion` (fuente única).
    enumOptions: TIPO_OPERACION_OPTIONS,
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      filterType: 'SelectMultipleFilter',
      label: 'Qué operación buscas',
      group: 'primary',
      // has_any: basta con que uno de los tipos aceptados coincida.
      searchMode: 'has_any',
    },
    showConfig: {
      label: 'Tipos de operación aceptados',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tipos de operación aceptados',
      placeholderMessage: 'Selecciona uno o varios…',
      // FASE2-WIZARD-2b: obligatorio. Un conjunto vacío sacaría al mandato
      // de toda búsqueda que filtrase por esta dimensión.
      isRequired: true,
      requiredMessage: 'Selecciona al menos un tipo de operación.',
    },
  },

  // Umbral de facturación que busca el comprador, expresado sobre la
  // escala ordinal de FACTURACION_SCALE (1..10). Son long para poder
  // cruzarlos por rango contra el pub_facturacionIdx de los negocios.
  {
    key: 'facturacionIdxMin',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    numberConfig: { minimum: FACTURACION_IDX_MIN, maximum: FACTURACION_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      label: 'Tu facturación anual',
      group: 'primary',
    },
    showConfig: {
      label: 'Facturación mínima buscada',
      isDetail: true,
    },
    saveConfig: {
      label: 'Facturación mínima buscada',
      placeholderMessage: 'Selecciona el tramo mínimo…',
    },
  },

  {
    key: 'facturacionIdxMax',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    numberConfig: { minimum: FACTURACION_IDX_MIN, maximum: FACTURACION_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: false,
      label: 'Facturación máxima buscada',
      group: 'primary',
    },
    showConfig: {
      label: 'Facturación máxima buscada',
      isDetail: true,
    },
    saveConfig: {
      label: 'Facturación máxima buscada',
      placeholderMessage: 'Selecciona el tramo máximo…',
    },
  },

  // FASE2-SCHEMA-01.5: EBITDA que busca el comprador, expresado sobre la
  // escala ordinal de EBITDA_SCALE (1..8). A diferencia de las otras tres
  // dimensiones del mandato, esta NO tiene par en el anuncio de venta: el
  // vendedor no declara su EBITDA, así que hoy no hay contra qué cruzarla.
  // Se guarda igual porque es criterio de búsqueda del comprador y lo
  // necesita el matching en cuanto el vendedor lo declare.
  {
    key: 'ebitdaIdxMin',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    numberConfig: { minimum: EBITDA_IDX_MIN, maximum: EBITDA_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      label: 'Tu EBITDA anual',
      group: 'primary',
    },
    showConfig: {
      label: 'EBITDA mínimo buscado',
      isDetail: true,
    },
    saveConfig: {
      label: 'EBITDA mínimo buscado',
      placeholderMessage: 'Selecciona el tramo mínimo…',
    },
  },

  {
    key: 'ebitdaIdxMax',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    numberConfig: { minimum: EBITDA_IDX_MIN, maximum: EBITDA_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: false,
      label: 'EBITDA máximo buscado',
      group: 'primary',
    },
    showConfig: {
      label: 'EBITDA máximo buscado',
      isDetail: true,
    },
    saveConfig: {
      label: 'EBITDA máximo buscado',
      placeholderMessage: 'Selecciona el tramo máximo…',
    },
  },

  // Plantilla que busca el comprador, expresada sobre la escala ordinal
  // de EMPLEADOS_SCALE (1..5) — NO en número de empleados.
  // FASE2-SCHEMA-01.2: cruzan contra pub_empleadosIdx del negocio en
  // venta, así que ambos lados comparan posiciones de la misma escala.
  {
    key: 'empleadosMin',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    numberConfig: { minimum: EMPLEADOS_IDX_MIN, maximum: EMPLEADOS_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      label: 'Tu plantilla',
      group: 'primary',
    },
    showConfig: {
      label: 'Nº mínimo de empleados',
      isDetail: true,
    },
    saveConfig: {
      label: 'Nº mínimo de empleados',
      placeholderMessage: 'Indica el mínimo',
    },
  },

  {
    key: 'empleadosMax',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    numberConfig: { minimum: EMPLEADOS_IDX_MIN, maximum: EMPLEADOS_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: false,
      label: 'Nº máximo de empleados',
      group: 'primary',
    },
    showConfig: {
      label: 'Nº máximo de empleados',
      isDetail: true,
    },
    saveConfig: {
      label: 'Nº máximo de empleados',
      placeholderMessage: 'Indica el máximo',
    },
  },

  // Antigüedad que busca el comprador, expresada sobre la escala ordinal
  // de ANTIGUEDAD_SCALE (1..5). Son long para poder cruzarlos por rango
  // contra el tramo del negocio en venta.
  {
    key: 'antiguedadIdxMin',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    numberConfig: { minimum: ANTIGUEDAD_IDX_MIN, maximum: ANTIGUEDAD_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: true,
      label: 'Años de tu negocio',
      group: 'primary',
    },
    showConfig: {
      label: 'Antigüedad mínima buscada',
      isDetail: true,
    },
    saveConfig: {
      label: 'Antigüedad mínima buscada',
      placeholderMessage: 'Selecciona el tramo mínimo…',
    },
  },

  {
    key: 'antiguedadIdxMax',
    scope: 'public',
    schemaType: 'long',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    numberConfig: { minimum: ANTIGUEDAD_IDX_MIN, maximum: ANTIGUEDAD_IDX_MAX },
    filterConfig: {
      indexForSearch: true,
      showFilter: false,
      label: 'Antigüedad máxima buscada',
      group: 'primary',
    },
    showConfig: {
      label: 'Antigüedad máxima buscada',
      isDetail: true,
    },
    saveConfig: {
      label: 'Antigüedad máxima buscada',
      placeholderMessage: 'Selecciona el tramo máximo…',
    },
  },

  // Quién es el comprador. Campo CALCULADO: se rellenará por código
  // desde el user type al publicar (sprint posterior), nunca a mano.
  // Sin saveConfig a propósito — EditListingDetailsForm exige
  // `hasSaveConfig` para renderizar, así que su ausencia lo mantiene
  // fuera del wizard. Mismo patrón que `verificado`.
  {
    key: 'perfil_comprador',
    scope: 'public',
    schemaType: 'enum',
    listingTypeConfig: {
      limitToListingTypeIds: true,
      listingTypeIds: ['mandato-compra'],
    },
    enumOptions: [
      { option: 'propietario',    label: 'Empresa o autónomo' },
      { option: 'inversor',       label: 'Inversor particular' },
      { option: 'broker',         label: 'Broker' },
      { option: 'family_office',  label: 'Family Office' },
      { option: 'fondo_inversion', label: 'Fondo de inversión' },
    ],
    filterConfig: {
      indexForSearch: true,
      // FASE2-BUSCADOR-3e: octavo filtro del feed de compra. Enum sin
      // `filterType`, así que lo atiende SelectMultipleFilter: el vendedor
      // puede quedarse solo con fondos y family offices, o descartarlos.
      showFilter: true,
      // Rompe a propósito el "tu" de los otros siete —no es un dato del
      // vendedor, sino de quien publica el mandato— pero mantiene el "tu
      // negocio" para decir por qué le importa: es quien va a llamar a su
      // puerta. En la ficha del anuncio sigue leyéndose "Perfil del
      // comprador", que es otra superficie y otro lector.
      label: 'Quién busca tu negocio',
      group: 'primary',
    },
    showConfig: {
      label: 'Perfil del comprador',
      isDetail: true,
    },
  },

];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

// FASE2-WIZARD-2a: los `label` son visibles para quien publica — el wizard los
// muestra como texto fijo bajo "Tipo de anuncio" (EditListingDetailsForm) — y
// pasarían a verse en el buscador en cuanto se habilite listingTypeFilter, que
// alimenta sus opciones con este mismo valor (configHelpers.js).
//
// ⚠️ MANTENER EN SINCRONÍA CON CONSOLE. En mergeListingConfig los tipos locales
// PISAN a los del asset (union prefiere el segundo array), así que lo que se
// lea aquí es lo que ve el usuario aunque Console diga otra cosa. Si los dos
// divergen, Console pasa a ser una fuente muerta que engaña a quien la consulte.
export const listingTypes = [
  {
    listingType: 'free-listing',
    label: 'Anuncio de venta',
    transactionType: {
      process: 'default-inquiry',
      alias: 'default-inquiry/release-1',
      unitType: 'inquiry',
    },
    defaultListingFields: {
      price: false,
      location: false,
      description: false,
      title: false,
      images: false,
    },
  },
  {
    // Antes solo existía en Console, así que su label lo ponía el asset
    // ("Mandato de compra"). Se declara aquí para que los dos tipos tengan una
    // única fuente y no dependan de qué lado gane el merge.
    // defaultListingFields replica lo que ya tenía el asset: solo `title` en
    // true (el título lo genera el sistema desde sector + provincias).
    listingType: 'mandato-compra',
    label: 'Anuncio de compra',
    transactionType: {
      process: 'default-inquiry',
      alias: 'default-inquiry/release-1',
      unitType: 'inquiry',
    },
    defaultListingFields: {
      price: false,
      location: false,
      description: false,
      title: true,
      images: false,
    },
  },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
